import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Users, 
  Download, 
  Search,
  Mail,
  TrendingUp,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistAdminPage({ user }) {
  const navigate = useNavigate();
  const [signups, setSignups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterVolume, setFilterVolume] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchSignups = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        ...(searchTerm && { search: searchTerm }),
        ...(filterType !== "all" && { investor_type: filterType }),
        ...(filterVolume !== "all" && { deal_volume: filterVolume })
      });

      const response = await fetch(`${API_URL}/api/waitlist/admin/signups?${params}`, {
        credentials: "include"
      });

      if (response.status === 401 || response.status === 403) {
        // Only redirect to login if user is not admin
        if (!user?.is_admin) {
          navigate("/login");
        }
        return;
      }

      const data = await response.json();
      setSignups(data.signups || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error("Failed to fetch signups:", err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/waitlist/admin/stats`, {
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSignups(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [page, filterType, filterVolume]);

  const handleSearch = () => {
    setPage(1);
    fetchSignups();
  };

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/api/waitlist/admin/export`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dealforge_waitlist_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const investorTypeLabels = {
    wholesaler: "Wholesaler",
    fix_flip: "Fix & Flip",
    buy_hold: "Buy & Hold",
    creative_finance: "Creative Finance",
    agent_investor: "Agent-Investor",
    other: "Other"
  };

  const volumeLabels = {
    "0-1": "0–1 deals",
    "2-5": "2–5 deals",
    "6-10": "6–10 deals",
    "10+": "10+ deals"
  };

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">Waitlist Dashboard</h1>
            <p className="text-white/70 mt-1">Manage early access signups</p>
          </div>
          <Button 
            onClick={handleExportCSV}
            className="bg-[#0A0A0A]/80 backdrop-blur-sm text-white hover:bg-white/[0.06] border border-white/[0.08]"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-black border border-white/[0.06] p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-gold" />
                <span className="text-white/70 text-sm">Total Signups</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-black border border-white/[0.06] p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="text-white/70 text-sm">This Week</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.this_week}</p>
            </div>
            <div className="bg-black border border-white/[0.06] p-6">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                <span className="text-white/70 text-sm">Emails Sent</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.emails_sent}</p>
            </div>
            <div className="bg-black border border-white/[0.06] p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-gold" />
                <span className="text-white/70 text-sm">High Volume (10+)</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.high_volume}</p>
            </div>
          </div>
        )}

        {/* Segmentation Breakdown */}
        {stats && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* By Investor Type */}
            <div className="bg-black border border-white/[0.06] p-6">
              <h3 className="font-heading text-lg font-bold text-white mb-4">By Investor Type</h3>
              <div className="space-y-3">
                {Object.entries(stats.by_investor_type || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-white/70">{investorTypeLabels[type] || type}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gold"
                          style={{ width: `${(count / stats.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-white font-mono w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* By Deal Volume */}
            <div className="bg-black border border-white/[0.06] p-6">
              <h3 className="font-heading text-lg font-bold text-white mb-4">By Deal Volume</h3>
              <div className="space-y-3">
                {Object.entries(stats.by_deal_volume || {}).map(([volume, count]) => (
                  <div key={volume} className="flex items-center justify-between">
                    <span className="text-white/70">{volumeLabels[volume] || volume}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500"
                          style={{ width: `${(count / stats.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-white font-mono w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-black border border-white/[0.06] p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-white/50" />
              <Input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-10 rounded-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-white/50" />
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-10 w-[160px] rounded-none">
                  <SelectValue placeholder="Investor Type" />
                </SelectTrigger>
                <SelectContent className="bg-black border-white/[0.08]">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="wholesaler">Wholesaler</SelectItem>
                  <SelectItem value="fix_flip">Fix & Flip</SelectItem>
                  <SelectItem value="buy_hold">Buy & Hold</SelectItem>
                  <SelectItem value="creative_finance">Creative Finance</SelectItem>
                  <SelectItem value="agent_investor">Agent-Investor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterVolume} onValueChange={setFilterVolume}>
                <SelectTrigger className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-10 w-[140px] rounded-none">
                  <SelectValue placeholder="Deal Volume" />
                </SelectTrigger>
                <SelectContent className="bg-black border-white/[0.08]">
                  <SelectItem value="all">All Volumes</SelectItem>
                  <SelectItem value="0-1">0–1 deals</SelectItem>
                  <SelectItem value="2-5">2–5 deals</SelectItem>
                  <SelectItem value="6-10">6–10 deals</SelectItem>
                  <SelectItem value="10+">10+ deals</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={() => { setPage(1); fetchSignups(); }}
              variant="ghost"
              className="text-white/70 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Signups Table */}
        <div className="bg-black border border-white/[0.06] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/50">Loading...</div>
          ) : signups.length === 0 ? (
            <div className="p-12 text-center text-white/50">No signups found</div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-[#0A0A0A]/80 backdrop-blur-sm/50">
                  <tr>
                    <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Email</th>
                    <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Investor Type</th>
                    <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Deal Volume</th>
                    <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Signed Up</th>
                    <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {signups.map((signup) => (
                    <tr key={signup.waitlist_id} className="hover:bg-[#0A0A0A]/80 backdrop-blur-sm/30">
                      <td className="px-6 py-4 text-white">{signup.email}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-gold/10 text-gold text-xs font-medium">
                          {investorTypeLabels[signup.investor_type] || signup.investor_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-500/10 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] text-xs font-medium">
                          {volumeLabels[signup.deal_volume] || signup.deal_volume}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white/70 text-sm">
                        {formatDate(signup.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium ${
                          signup.email_sent 
                            ? "bg-green-500/10 text-green-400" 
                            : "bg-white/[0.08] text-white/50"
                        }`}>
                          {signup.email_sent ? "Email Sent" : "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-white/[0.06]">
                  <span className="text-white/50 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      variant="ghost"
                      className="text-white/70 hover:text-white disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      variant="ghost"
                      className="text-white/70 hover:text-white disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
